import React, { useEffect, useRef } from 'react';
import { Box, Container, Typography, Grid, IconButton } from '@mui/material';
import { motion, useScroll, useTransform, useAnimation } from 'framer-motion';
import mainVideo from '../assets/mainsection.mp4';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const Banner = () => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const controls = useAnimation();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 1, delay: 0.5 }
    });
  }, [controls]);

  const scrollToNext = () => {
    const nextSection = document.getElementById('next-section');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.5
      }
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'black'
      }}
    >
      {/* Video Background with Enhanced Effects */}
      <Box
        component={motion.div}
        style={{ opacity, scale }}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(26, 71, 42, 0.4) 0%, rgba(26, 71, 42, 0.7) 100%)',
            zIndex: 1
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at center, transparent 30%, rgba(0,0,0,0.4) 100%)',
            zIndex: 2
          }
        }}
      >
        <motion.video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse" }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        >
          <source src={mainVideo} type="video/mp4" />
        </motion.video>
      </Box>

      {/* Content with Enhanced Animations */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          style={{ width: '100%' }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={10}>
              <Box>
                <motion.div variants={textVariants}>
                  <Typography
                    variant="h1"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
                      lineHeight: 1.2,
                      mb: 3,
                      position: 'relative',
                      display: 'inline-block',
                      textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      '& .highlight': {
                        color: '#4CAF50',
                        position: 'relative',
                        display: 'inline-block',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          height: '4px',
                          background: 'linear-gradient(90deg, #4CAF50, transparent)',
                          transform: 'scaleX(0)',
                          transformOrigin: 'left',
                          animation: 'expandWidth 1.5s forwards'
                        }
                      },
                      '@keyframes expandWidth': {
                        '0%': {
                          transform: 'scaleX(0)'
                        },
                        '100%': {
                          transform: 'scaleX(1)'
                        }
                      }
                    }}
                  >
                    Driven By <span className="highlight">Expertise</span>,<br />
                    Powered By <span className="highlight">Passion</span>
                  </Typography>
                </motion.div>

                <motion.div 
                  variants={textVariants}
                  sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -20,
                      top: 0,
                      width: '4px',
                      height: '100%',
                      background: 'linear-gradient(180deg, #4CAF50, transparent)'
                    }
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      maxWidth: '800px',
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                      lineHeight: 1.6,
                      fontWeight: 400,
                      pl: 3,
                      borderLeft: '4px solid rgba(76, 175, 80, 0.3)',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
                    }}
                  >
                    Our Team Of Skilled Engineers And Professionals Is Dedicated To<br />
                    Delivering Precision, Reliability, And Innovation.
                  </Typography>
                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>

      {/* Enhanced Scroll Indicator */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        sx={{
          position: 'absolute',
          bottom: { xs: 20, md: 40 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          textAlign: 'center'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            mb: 1,
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.2em'
          }}
        >
          Explore More
        </Typography>
        <IconButton
          onClick={scrollToNext}
          component={motion.div}
          animate={{
            y: [0, 10, 0]
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: "reverse"
          }}
          sx={{
            color: 'white',
            bgcolor: 'rgba(76, 175, 80, 0.2)',
            backdropFilter: 'blur(5px)',
            '&:hover': {
              bgcolor: 'rgba(76, 175, 80, 0.3)'
            }
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Banner;
